import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, computed, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import {
	PrismButtonComponent,
	PrismDialogComponent,
	PrismInputComponent,
	PrismRadioButtonComponent,
	PrismDialogRef,
	PrismRadioGroupComponent,
	TextValueAccessor,
	PRISM_DIALOG_DATA
} from '@shure/prism-angular-components';

import { noWhitespaceValidator } from '../../utils';

import { SearchDialogData, SearchField } from './search-dialog.model';

@Component({
	selector: 'sh-search-dialog',
	standalone: true,
	imports: [
		CommonModule,
		PrismButtonComponent,
		PrismDialogComponent,
		PrismInputComponent,
		PrismRadioButtonComponent,
		TranslocoModule,
		PrismRadioGroupComponent,
		FormsModule,
		ReactiveFormsModule,
		TextValueAccessor
	],
	templateUrl: './search-dialog.component.html',
	styleUrls: ['./search-dialog.component.scss']
})
export class SearchDialogComponent implements OnInit {
	public selectedField = signal<SearchField | ''>('');
	public title = '';

	public selectFieldLabel = computed(() => {
		const selected = this.selectedField();
		return this.dialogData?.fields?.find((f) => f.name === selected)?.label || '';
	});

	public searchForm = new FormGroup({
		emailId: new FormControl('', [
			Validators.required,
			Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$')
		]),
		userId: new FormControl('', [Validators.required, noWhitespaceValidator()]),
		organizationId: new FormControl('', [Validators.required, noWhitespaceValidator()]),
		displayName: new FormControl('', [Validators.required, noWhitespaceValidator()])
	});

	public selectedControl = computed(() => {
		const field = this.selectedField();
		return field ? this.searchForm.get(field) : null;
	});

	public validationHintMessage = computed(() => {
		const field = this.selectedField();
		if (!field) return '';
		const control = this.searchForm.get(field);
		if (!control || !control.touched || control.valid) return '';

		if (control.hasError('required') || control.hasError('whitespace')) {
			return this.getValidationHintMessage(field);
		}
		if (field === 'emailId' && control.hasError('pattern')) {
			return this.translocoService.translate('cloud.shared.error-labels.enter-a-valid-email-id');
		}
		return '';
	});

	constructor(
		private translocoService: TranslocoService,
		private dialogRef: PrismDialogRef<SearchDialogComponent>,
		@Inject(PRISM_DIALOG_DATA) public dialogData: SearchDialogData
	) {}
	public ngOnInit(): void {
		this.title = this.dialogData.title;
	}

	/**
	 * Triggered when a radio option is selected.
	 * Resets the previous input and sets the new selected field.
	 */
	public onFieldSelection(value: string): void {
		this.selectedField.set(<SearchField>value);
		this.resetForm();
	}

	/**
	 * Updates the form control corresponding to the currently selected field
	 * with the given input value. It also marks the control as dirty and touched,
	 * and triggers validation updates.
	 *
	 * @param value - The new value entered by the user. If null or undefined, an empty string is used.
	 */
	public onInputChange(value: string): void {
		const control = this.selectedControl();
		if (!control) return;
		control.setValue(value || '');
		control.markAsDirty();
		control.markAsTouched();
		control.updateValueAndValidity();
	}

	/**
	 * Closes the dialog either with selected field data or cancels it based on the submitType.
	 */
	public handleClose(submitType = ''): void {
		if (!submitType) {
			this.dialogRef.close('');
			return;
		}
		const value = this.searchForm.get(this.selectedField())?.value;
		this.dialogRef.close({ field: this.selectedField(), value });
	}

	/**
	 * Determines whether the submit button should be enabled.
	 *
	 * It checks the form control associated with the currently selected field,
	 * and ensures:
	 * - the control exists,
	 * - the control is valid,
	 * - the control has a non-empty, trimmed value.
	 *
	 * @returns `true` if the submit button should be enabled, otherwise `false`.
	 */
	public get isSubmitEnabled(): boolean {
		const selected = this.selectedField();
		const control = this.searchForm.get(selected);
		return !!control && control.valid && !!control.value && String(control.value).trim().length > 0;
	}

	/**
	 * Consolidated error handler: returns final error message string
	 * for the selected field based on control state.
	 */
	public getValidationHintMessage(field?: SearchField | ''): string {
		switch (field) {
			case 'displayName':
				return this.translocoService.translate('cloud.shared.error-labels.displayName-required');
			case 'emailId':
				return this.translocoService.translate('cloud.shared.error-labels.emailId-required');
			case 'organizationId':
				return this.translocoService.translate('cloud.shared.error-labels.organizationId-required');
			case 'userId':
				return this.translocoService.translate('cloud.shared.error-labels.userId-required');
			default:
				return '';
		}
	}
	/**
	 * Resets the form control of the previously selected field to its pristine state.
	 */
	public resetForm(): void {
		Object.keys(this.searchForm.controls).forEach((field) => {
			const control = this.searchForm.get(field);
			control?.reset();
			control?.setErrors(null);
			control?.markAsPristine();
			control?.markAsUntouched();
		});
	}
}
